import { X } from 'lucide-react'

import { cn } from 'lib/utils'

import type { AssetName } from '../../hooks/useAssetNames'
import type { BlockView } from '../../hooks/useResolvedBlocks'
import { storySpans, type PresentedSlide } from '../../hooks/usePresenter'
import type { EntityRef } from '../../types/presentation.types'
import { BlockRenderModeProvider } from '../../blocks/renderMode'
import { PresentedSlideView } from './PresentedSlideView'

/**
 * Every slide at a glance, grouped by story (MYD-496). Opened with G; a click
 * jumps there. Thumbnails are drawn exactly as the slides will be, story
 * dividers included (MYD-497).
 */
interface OverviewGridProps {
	slides: PresentedSlide[]
	current: number
	blocks: Record<string, BlockView | undefined>
	theme?: string
	storyCount: number
	assetName: (ref: EntityRef | null) => AssetName | null
	onPick: (index: number) => void
	onClose: () => void
}

const THUMB_WIDTH = 224

export function OverviewGrid({ slides, current, blocks, theme, storyCount, assetName, onPick, onClose }: OverviewGridProps) {
	return (
		<div className="absolute inset-0 z-30 overflow-y-auto bg-neutral-950 px-8 py-6" role="dialog" aria-label="All slides">
			<div className="mb-4 flex items-center justify-between text-white">
				<h2 className="text-sm font-semibold uppercase tracking-wide text-white/70">All slides</h2>
				<button type="button" onClick={onClose} className="rounded p-1.5 text-white/70 hover:bg-white/10 hover:text-white" aria-label="Close overview">
					<X className="h-5 w-5" />
				</button>
			</div>

			{storySpans(slides).map(span => (
				<section key={`${span.storyIndex}-${span.start}`} className="mb-6">
					<h3 className="mb-2 text-xs font-medium text-white/60">{span.title}</h3>
					<div className="flex flex-wrap gap-4">
						{slides.slice(span.start, span.start + span.count).map((entry, offset) => {
							const index = span.start + offset
							const label = entry.divider ? 'Story divider' : entry.slide.title
							return (
								<button
									key={entry.slide.id}
									type="button"
									onClick={() => onPick(index)}
									aria-label={`Go to slide ${index + 1}${label ? `: ${label}` : ''}`}
									aria-current={index === current ? 'true' : undefined}
									className={cn('group rounded-md p-1 text-left transition-colors', index === current ? 'bg-primary' : 'hover:bg-white/10')}
								>
									{/* Thumbnails: a live map per slide would exhaust the page's WebGL contexts. */}
									<BlockRenderModeProvider value="thumbnail">
										<PresentedSlideView
											entry={entry}
											width={THUMB_WIDTH}
											blocks={blocks}
											theme={theme}
											storyCount={storyCount}
											assetName={assetName}
											className="pointer-events-none overflow-hidden rounded-sm"
										/>
									</BlockRenderModeProvider>
									<span className="mt-1 block px-0.5 text-[11px] text-white/80">
										{index + 1}
										{label && <span className="text-white/50"> · {label}</span>}
									</span>
								</button>
							)
						})}
					</div>
				</section>
			))}
		</div>
	)
}

export default OverviewGrid
